import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';
import styled from 'styled-components';

import {
  cioFacetsToUrlFilters,
  cioSizeFacetsToFilterPreferences,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';
import { CUSTOM_COLOR_FILTER } from '~/gridv1_1/constants';
import useGridInputs, { GRID_TYPES } from '~/gridv1_1/hooks/useGridInputs';
import { QueryParamBehavior } from '~/gridv1_1/utils/routing';
import { sizeByKey } from '~/shared/utils/sizeConstants';
import useGender from '~/shared/utils/useGender';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  clearAllFacetsFromUrlParams,
  getRbidByFacetNameAndFacetOption,
  getSelectedFacetRbid,
  getTransformedFacetOptionDisplayName,
  isCioSizeFacet,
  updateFiltersInUrlParams,
} from '../../utils/filters';
import { scrollToTopOfGrid } from '../../utils/scroll';

const DynamicFormattedMessage = FormattedMessage;

const DesktopGridSelectedFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersTextColor};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Heading = styled.p`
  font-size: 1rem;
  ${({ theme }) => theme.gridFilterStyles.headerStyle};
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  text-decoration: underline;
  font-family: inherit;
  ${({ theme }) => theme.gridFilterStyles.clearAllButtonStyle};
`;

const FilterList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 1rem 0;
`;

const FilterItem = styled.li`
  display: flex;
  align-items: center;
  line-height: 16px;
  padding: 4px 8px;
  font-size: 0.75rem;
  white-space: pre-wrap;
  ${({ theme }) => theme.gridFilterStyles.tagStyle};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersTextColor};
  cursor: pointer;
  background: none;
  font-family: inherit;
  ${({ theme }) => theme.typography.variants.gridEmphasized.textStyle};
`;

const Icon = styled(MdClose)`
  stroke-width: 2;
  margin-top: 0px;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersPillIconColor};
`;

const DesktopGridSelectedFilters = ({ closeAllPanels }) => {
  const {
    selectedUrlFilters,
    allRegisteredFacetNames,
    allRegisteredFacets,
    customFacetFilters,
    setCustomFacetFilters,
  } = useUnifiedProductGridData();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const domain = useDomain();
  const { gender } = useGender();
  const { gridType } = useGridInputs();

  const allClearableFacetNames = [
    ...allRegisteredFacetNames,
    CUSTOM_COLOR_FILTER.name,
  ];

  const selectedFacets = Object.entries(selectedUrlFilters).flatMap(
    ([key, value]) => {
      const facetName = urlFiltersToCioFacets[key] || key;
      const facet = allRegisteredFacets?.find(
        (facet) =>
          facet?.name ===
          (gridType === GRID_TYPES.PRODUCTS_V2_GRID ? key : facetName)
      );

      if (facet) {
        return value
          ?.map((option) => {
            const categoryName = cioSizeFacetsToFilterPreferences[facet.name];

            const translatedName = sizeByKey({
              domain,
              gender,
              category: categoryName,
              key: option,
            });
            const cioOption = facet?.options?.find(
              (facetOption) => String(facetOption?.value) === String(option)
            );

            if (!cioOption) {
              return null;
            }

            return {
              facetName,
              urlName: key,
              facetOption: translatedName || option,
              facetOptionDisplayName: getTransformedFacetOptionDisplayName({
                selectedFacetName: facetName,
                selectedFacetDisplayName: facet?.display_name,
                selectedFacetOptionDisplayName: cioOption?.display_name,
              }),
            };
          })
          ?.filter(Boolean);
      }
      return [];
    }
  );

  const validCustomFacetFilters = Object.values(customFacetFilters).filter(
    (item) => !item.hasError
  );

  const handleClearAllFilters = () => {
    clearAllFacetsFromUrlParams({
      router,
      allRegisteredFacetNames: allClearableFacetNames,
      gridType,
    });
    setCustomFacetFilters({});
    closeAllPanels();
    scrollToTopOfGrid({ isMobile });
  };

  const handleOnClick = ({ facetName, facetOption }) => {
    updateFiltersInUrlParams({
      facetName,
      facetOption,
      router,
    });
    scrollToTopOfGrid({ isMobile });
  };

  if (!selectedFacets?.length && !Object.keys(validCustomFacetFilters).length) {
    return null;
  }

  return (
    <DesktopGridSelectedFiltersContainer>
      <HeaderRow>
        <Heading>
          <FormattedMessage
            id="site_product_filter.filtered_by"
            defaultMessage="Filtered by:"
          />
        </Heading>
        <ClearAllButton
          type="button"
          onClick={handleClearAllFilters}
          aria-label="Clear all filters"
          data-autotag="filter-clear-all"
        >
          <FormattedMessage
            id="site_product_filter.clear_all"
            defaultMessage="Clear all"
          />
        </ClearAllButton>
      </HeaderRow>
      <FilterList data-autotag="filtered-by-textbox">
        {selectedFacets?.map(
          ({ facetName, facetOption, facetOptionDisplayName }) => (
            <FilterItem
              key={`${facetName}-${facetOption}`}
              data-autotag="filtered-by-option"
            >
              {isCioSizeFacet(facetName) ? (
                <>
                  <DynamicFormattedMessage
                    id={`site_product_filter.${getRbidByFacetNameAndFacetOption(
                      facetName,
                      facetOption
                    )}`}
                    defaultMessage={facetOption}
                  />
                  {' ('}
                  <DynamicFormattedMessage
                    id={`site_product_filter.${
                      cioFacetsToUrlFilters[facetName] || facetName
                    }`}
                    defaultMessage={
                      cioFacetsToUrlFilters[facetName] || facetName
                    }
                  />
                  )
                </>
              ) : (
                <DynamicFormattedMessage
                  id={`site_product_filter.${getSelectedFacetRbid(
                    facetName,
                    facetOptionDisplayName
                  )}`}
                  defaultMessage={facetOptionDisplayName}
                />
              )}
              <RemoveButton
                type="button"
                onClick={() =>
                  handleOnClick({
                    facetName,
                    facetOption,
                  })
                }
                aria-label={`Remove ${facetOptionDisplayName} filter`}
                data-autotag="filtered-by-option-close-icon"
              >
                <Icon size={12} />
              </RemoveButton>
            </FilterItem>
          )
        )}
        {Object.values(validCustomFacetFilters).map((customFilter) => (
          <FilterItem key={customFilter.name} data-autotag="filtered-by-option">
            {customFilter.label}
            <RemoveButton
              type="button"
              onClick={() => {
                updateFiltersInUrlParams({
                  facetName: customFilter.name,
                  facetOption: [],
                  router,
                  queryParamBehavior: QueryParamBehavior.REPLACE,
                });
                setCustomFacetFilters(
                  ({ [customFilter.name]: _, ...rest }) => rest
                );
              }}
              aria-label={`Remove ${customFilter.name} filter`}
              data-autotag="filtered-by-option-close-icon"
            >
              <Icon size={12} />
            </RemoveButton>
          </FilterItem>
        ))}
      </FilterList>
    </DesktopGridSelectedFiltersContainer>
  );
};

DesktopGridSelectedFilters.propTypes = {
  closeAllPanels: PropTypes.func.isRequired,
};

export default DesktopGridSelectedFilters;
